import { StatusBadge } from '@/components/status-badge';
import { useQuotes, type Quote } from '@/hooks';
import { getProductIcon } from '@/lib/product-icon';
import { produits, quoteStatuts } from '@workspace/api';
import { useSearchHistory } from '@workspace/backoffice/hooks/use-search-history';
import { formatDate, getLabelFromValue } from '@workspace/utils';
import { Clock, Search, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';

/** En-dessous, la recherche rendrait tout le portefeuille */
const SEARCH_MIN_LENGTH = 2;
const RESULTS_PER_PAGE = 20;
const HISTORY_STORAGE_KEY = 'deals-search-history';

/** Nom du client, ou sa raison sociale pour une personne morale */
function getContactName(contact: Quote['contact'] | undefined): string {
    const name = [contact?.nom, contact?.prenom].filter(Boolean).join(' ');
    return contact?.raison_sociale || name || 'Client inconnu';
}

/** Véhicule du devis, en une ligne */
function getVehicleLine(quote: Quote): string {
    return [quote.immatriculation, [quote.marque_vehicule, quote.modele_vehicule].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
}

function ResultRow({ quote, onPick }: { quote: Quote; onPick: (path: string) => void }) {
    const Icon = getProductIcon(quote.code_produit);
    const vehicle = getVehicleLine(quote);

    const content = (
        <>
            <span className="flex size-9 shrink-0 items-center justify-center rounded-md bg-white/10">
                <Icon
                    className="size-4"
                    aria-hidden="true"
                />
            </span>
            <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2">
                    <span className="min-w-0 truncate font-medium">{getContactName(quote.contact)}</span>
                    <StatusBadge
                        value={quote.statut}
                        label={getLabelFromValue(quoteStatuts, quote.statut)}
                        type="statut"
                    />
                </span>
                <span className="block truncate text-xs text-white/55">
                    Devis n° {quote.id_devis} · {getLabelFromValue(produits, quote.code_produit)}
                    {quote.acte_gestion ? ` · ${quote.acte_gestion}` : ''}
                </span>
                {vehicle && <span className="block truncate text-xs text-white/45">{vehicle}</span>}
                <span className="block text-xs text-white/45">Créé le {formatDate(quote.created_at)}</span>
            </span>
        </>
    );

    // Un devis que l'utilisateur n'a pas le droit de consulter ne mène nulle part, comme sur la liste
    if (!quote.can_view_details) {
        return <div className="flex w-full items-center gap-3 rounded-lg bg-white/5 px-3 py-2.5 text-left opacity-50">{content}</div>;
    }

    return (
        <button
            type="button"
            onClick={() => onPick(`/quotes/${quote.id}`)}
            className="flex w-full items-center gap-3 rounded-lg bg-white/5 px-3 py-2.5 text-left transition-colors hover:bg-white/15"
        >
            {content}
        </button>
    );
}

/**
 * Popup de recherche, sur le modèle de l'espace conseiller : elle cherche dans Deals comme le
 * champ « Recherche globale » de la liste des devis (immat, n° devis, nom, email, tél, code promo).
 *
 * La recherche part à la validation (Entrée) et non à la frappe : on cherche un dossier précis,
 * pas en tâtonnant, et chaque frappe n'a pas à interroger l'API.
 */
export function SearchOverlay({ onClose }: { onClose: () => void }) {
    const navigate = useNavigate();
    const [term, setTerm] = useState('');
    const [query, setQuery] = useState('');
    const history = useSearchHistory(HISTORY_STORAGE_KEY);

    const tooShort = query.trim().length < SEARCH_MIN_LENGTH;
    const { data, isFetching, isError } = useQuotes({ search: query.trim(), per_page: RESULTS_PER_PAGE, page: 1 }, { enabled: !tooShort });
    const results = data?.data ?? [];
    const total = data?.meta?.total ?? results.length;

    const pick = useCallback(
        (path: string) => {
            onClose();
            navigate(path);
        },
        [onClose, navigate],
    );

    const search = (value: string) => {
        setTerm(value);
        setQuery(value);
        history.remember(value);
    };

    // Échap ferme, même si le focus a quitté le champ
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') onClose();
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [onClose]);

    // Un seul devis trouvé : on l'ouvre, l'utilisateur n'a pas à confirmer ce que la recherche a
    // déjà tranché. Jamais pendant un chargement ni sur une erreur, où `data` porte le résultat
    // de la requête précédente.
    const onlyResult = !isFetching && !isError && !tooShort && total === 1 && results.length === 1 ? results[0] : undefined;
    const autoTarget = onlyResult?.can_view_details ? `/quotes/${onlyResult.id}` : undefined;

    useEffect(() => {
        if (autoTarget) pick(autoTarget);
    }, [autoTarget, pick]);

    /*
     * Rendue dans `document.body` et non là où elle est montée : la navbar porte un
     * `backdrop-blur`, et un `backdrop-filter` fait de son élément le bloc conteneur de ses
     * descendants `position: fixed`. Le `fixed inset-0` se retrouvait alors enfermé dans la
     * hauteur du bandeau — la popup s'ouvrait en bande de 50 px au lieu de couvrir l'écran.
     */
    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Rechercher un client"
            data-state="open"
            // Pas de fermeture au clic sur le fond : une recherche se lit et se compare, un clic à
            // côté ne doit pas faire perdre les résultats. La croix (ou Échap) ferme.
            className="fixed inset-0 z-50 flex flex-col items-center bg-[#071922]/85 px-4 pt-24 text-white backdrop-blur-sm"
        >
            <button
                type="button"
                onClick={onClose}
                className="absolute right-5 top-5 rounded-md p-2 transition-colors hover:bg-white/10"
                aria-label="Fermer la recherche"
            >
                <X className="size-6" />
            </button>

            <div className="w-full max-w-4xl">
                <div className="relative">
                    <Search
                        className="absolute left-4 top-1/2 size-5 -translate-y-1/2 text-white/50"
                        aria-hidden="true"
                    />
                    <input
                        autoFocus
                        type="text"
                        value={term}
                        onChange={(event) => setTerm(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') search(term);
                        }}
                        placeholder="Immat, n° devis, nom, email, tél, code promo..."
                        className="h-12 w-full rounded-lg border border-white/20 bg-white/10 pl-11 pr-4 text-lg outline-none placeholder:text-white/50 focus:border-primary"
                    />
                </div>

                {!isFetching && !tooShort && results.length > 0 && (
                    <p className="mt-3 px-1 text-sm text-white/55">
                        {total} devis trouvé{total > 1 ? 's' : ''}
                        {total > results.length && <span className="text-white/45"> — affinez la recherche pour voir les autres</span>}
                    </p>
                )}

                {/* Recherche pas encore lancée : on propose les dernières, on rouvre souvent le
                    même dossier dans la journée. */}
                {tooShort && history.entries.length > 0 && (
                    <div className="mt-4">
                        <div className="flex items-center justify-between px-1">
                            <p className="text-sm text-white/55">Recherches récentes</p>
                            <button
                                type="button"
                                onClick={history.clear}
                                className="text-sm text-white/55 underline-offset-4 hover:text-white hover:underline"
                            >
                                Effacer
                            </button>
                        </div>
                        <ul className="mt-2 flex flex-wrap gap-2">
                            {history.entries.map((entry) => (
                                <li key={entry}>
                                    <button
                                        type="button"
                                        onClick={() => search(entry)}
                                        className="flex items-center gap-2 rounded-lg border border-white/20 bg-white/10 px-3 py-1.5 text-sm transition-colors hover:bg-white/20"
                                    >
                                        <Clock
                                            className="size-3.5 text-white/50"
                                            aria-hidden="true"
                                        />
                                        {entry}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="mt-2 max-h-[60vh] overflow-y-auto pr-1">
                    {tooShort ? null : isFetching ? (
                        <div className="flex justify-center py-8">
                            <div className="size-8 animate-spin rounded-full border-2 border-white border-t-transparent" />
                            <span className="sr-only">Recherche...</span>
                        </div>
                    ) : isError ? (
                        <p
                            className="px-3 py-6 text-center text-sm text-red-300"
                            role="alert"
                        >
                            La recherche a échoué. Réessayez dans un instant.
                        </p>
                    ) : results.length > 0 ? (
                        <ul className="grid gap-2 sm:grid-cols-2">
                            {results.map((quote) => (
                                <li key={quote.id}>
                                    <ResultRow
                                        quote={quote}
                                        onPick={pick}
                                    />
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="px-3 py-6 text-center text-sm text-white/55">Aucun devis trouvé.</p>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}
